import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {HOTB} from "../HOTB";
import {ResizeHelper} from "../../core/debug/ResizeHelper";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {AnimUtils} from "../utils/AnimUtils";
import {ExtendedGameMode} from "../server/ExtendedGameMode";
import {gsap} from "gsap";
import {HOTBData} from "../server/HOTBData";
import {GameUtils} from "../utils/GameUtils";
import {GameSounds} from "../sound/GameSounds";
import {GameConfig} from "../GameConfig";
import {BattleTank} from "./BattleTank";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import TrackEntry = PIXI.spine.core.TrackEntry;

export class Background extends PIXI.Container implements StageLayer {
    private _bgSpineAnim: PIXI.spine.Spine;
    private _satanAnim: PIXI.spine.Spine;
    private _gameLogo: PIXI.Sprite;

    private _leftTank: BattleTank;
    private _rightTank: BattleTank;
    private _isTankPlaying: boolean = false;
    private _tanksTL: gsap.core.Timeline;

    constructor() {
        super();
        this.name = "Background";
        this.init();
    }

    private _bgTransitionName: string;

    get bgTransitionName(): string {
        return this._bgTransitionName;
    }

    set bgTransitionName(value: string) {
        this._bgTransitionName = value;
    }

    public getCurrentAnimation(): string {
        const currentAnimation: TrackEntry = this._bgSpineAnim.state.getCurrent(0);
        return currentAnimation ? currentAnimation.animation.name : '';
    }

    onResize(): void {
        this._gameLogo.visible = true;
        this._gameLogo.scale.set(1);
        if (NolimitApplication.isLandscape) {
            this._gameLogo.position.set(-150, 180);
            if (ResizeHelper.isIPadDevice) {
                this._gameLogo.visible = false;
            }
        } else {
            this._gameLogo.position.set(360, -130);
            if (ResizeHelper.isIPadDevice) {
                this._gameLogo.position.set(360, -50);
                this._gameLogo.scale.set(0.8);
            }
        }
    }

    public playBGTransition(): gsap.core.Timeline {
        const hotbData: HOTBData = HOTB.model.data,
            currMode: ExtendedGameMode = hotbData.mode,
            nextMode: ExtendedGameMode = hotbData.nextMode,
            animName: string = GameUtils.getGamePrefix(currMode) + "_to_" + GameUtils.getGamePrefix(nextMode),
            tl: gsap.core.Timeline = gsap.timeline({
                onStart: (): void => {
                    this.toggleLogoVisibility(false);
                    HOTB.glassOverlay.toggleVisibility(false);
                    if (nextMode === ExtendedGameMode.NORMAL) {
                        HOTB.gameForeground.hideDoors();
                    }
                },
                onComplete: (): void => {
                    this.onBGTransitionComplete(nextMode);
                }
            });

        tl.add([
            AnimUtils.playSpineAnimation({
                spineObj: this._bgSpineAnim,
                animation: animName
            }),
            (): void => {
                this._leftTank.toggleDisplay(false);
                this._rightTank.toggleDisplay(false);
                this.bgTransitionName = animName;
                this.playBGSounds(animName);
            }
        ]);

        return tl;
    }

    public playIdle(mode: ExtendedGameMode): void {
        const gameType: string = GameUtils.getGamePrefix(mode);

        AnimUtils.playSpineAnimation({
            spineObj: this._bgSpineAnim,
            animation: gameType + "_idle",
            playLoop: true
        });

        if (gameType === "mg") {
            this._leftTank.toggleDisplay(true);
            this._rightTank.toggleDisplay(true);
        }
    }

    public toggleSatanBackground(show: boolean): void {
        if (show) {
            AnimUtils.playSpineAnimation({
                spineObj: this._satanAnim,
                animation: "idle",
                playLoop: true
            });
            this.resetTanksIdle();
        } else {
            AnimUtils.resetSpine(this._satanAnim);
        }
    }

    public setTanksIdleTimeline(): void {
        if (this._isTankPlaying || !GameConfig.isGameIdle || GameUtils.isSatanSpin()) {
            return;
        }
        let countdown: number = GameConfig.tanksDelay;

        this._tanksTL = gsap.timeline();

        for (let i: number = 0; i <= countdown; i++) {
            this._tanksTL.add((): void => {
                Logger.logDev(`Tank movement in ${countdown - i} seconds...`, GameUtils.getTimeStamp());
            }, `+=1`);
        }

        this._tanksTL.add((): void => {
            this.startMovingTanks();
        });
    }

    public onSpinStart(): void {
        this.handleTanksVisibility();
        this.resetTanksIdle();
    }

    private resetTanksIdle(): void {
        if (this._tanksTL) {
            this._tanksTL.pause().kill();
        }
    }

    private handleTanksVisibility(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;

        /*Logger.logDev("-tanks onSpinStart bounds-", bounds.left, bounds.right);
        Logger.logDev("-tanks onSpinStart positions-", this._leftTank.position.x, this._rightTank.position.x);
        Logger.logDev("-tanks onSpinStart other-", this._isTankPlaying, GameConfig.isGameIdle);*/

        const leftStartPosX: number = bounds.left - 30,
            rightStartPosX: number = bounds.right + 30;

        if (this._leftTank.position.x <= leftStartPosX && this._rightTank.position.x >= rightStartPosX) {
            this._leftTank.toggleDisplay(false);
            this._rightTank.toggleDisplay(false);
        }
    }

    private getRandomDelay(): number {
        return GameUtils.getRandomBoolean(50) ? 0 : GameUtils.uniqueNumberInRange(1, 3);
    }

    private startMovingTanks(): void {
        this._isTankPlaying = true;

        this._leftTank.toggleDisplay(true);
        this._rightTank.toggleDisplay(true);

        const tl: gsap.core.Timeline = gsap.timeline();

        const leftDelay: number = this.getRandomDelay(),
            rightDelay: number = leftDelay === 0 ? this.getRandomDelay() : 0;

        tl.add(this._leftTank.moveTank(), leftDelay);
        tl.add(this._rightTank.moveTank(), rightDelay);

        tl.add((): void => {
            this._isTankPlaying = false;
            Logger.logDev(`Tank movement stopped`, GameUtils.getTimeStamp());
            this.setTanksIdleTimeline();
        }, `>`);
    }

    private toggleLogoVisibility(show: boolean): void {
        this._gameLogo.alpha = show ? 1 : 0;
    }

    private playBGSounds(type: string): void {
        if (type === "mg_to_fs") {
            HOTB.soundPlayer.playSound(GameSounds.MOVE_MAIN_FS);
        }

        if (type === "mg_to_sfs") {
            HOTB.soundPlayer.playSound(GameSounds.MOVE_MAIN_SFS);
        }

        if (type === "fs_to_mg") {
            HOTB.soundPlayer.playSound(GameSounds.MOVE_FS_MAIN);
        }

        if (type === "sfs_to_mg") {
            HOTB.soundPlayer.playSound(GameSounds.MOVE_SFS_MAIN);
        }

        if (type === "fs_to_sfs") {
            HOTB.soundPlayer.playSound(GameSounds.MOVE_FS_SFS);
        }
    }

    private onBGTransitionComplete(nextMode: ExtendedGameMode): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add([
            HOTB.gameForeground.showStaticDoors(nextMode, true),
            (): void => {
                HOTB.glassOverlay.toggleVisibility(true);
                this.toggleLogoVisibility(true);
            }
        ]);

        tl.add((): void => {
            this.playIdle(nextMode);
        });

        return tl;
    }

    private init(): void {
        this.createBackground();
        this.createTanks();
        this.createSatanBackground();
        this.createGameLogo();

        this.onResize();
    }

    private createBackground(): void {
        this._bgSpineAnim = AnimUtils.createNewSpine({
            path: "bgSpine",
            name: "bgSpine",
            pos: new PIXI.Point(360, 360),
            initShow: true
        });

        this.addChild(this._bgSpineAnim);
    }

    private createTanks(): void {
        this._leftTank = new BattleTank("left");
        this._rightTank = new BattleTank("right");
        this.addChild(this._leftTank, this._rightTank);
    }

    private createSatanBackground(): void {
        this._satanAnim = AnimUtils.createNewSpine({
            path: "satanSpine",
            name: "satanBg",
            pos: new PIXI.Point(360, 360),
            initShow: false
        });

        this.addChild(this._satanAnim);
    }

    private createGameLogo(): void {
        this._gameLogo = new PIXI.Sprite(HOTB.assetLoader.getTextures("gameLogo")[0]);
        this._gameLogo.position.set(360, 360);
        this._gameLogo.anchor.set(0.5, 0.5);

        this.addChild(this._gameLogo);
    }
}